import { createContext, useCallback, useContext, useState } from "react";

import type { ReactNode } from "react";

// A counter the sidenav bumps on every navigation; pages list it in the dependency
// array of the effect that loads their data. The bump function is in its own context
// so that the sidenav links, which only ever bump it, never re-render because of it.
const ReloadContext = createContext<number | null>(null);
const ReloadNowContext = createContext<(() => void) | null>(null);

ReloadContext.displayName = "ReloadContext";
ReloadNowContext.displayName = "ReloadNowContext";

function ReloadProvider({ children }: { children: ReactNode }) {
  // It started life as `useState(false)` and was then incremented, so the first bump
  // produced `false + 1`. A number from the start says what it is.
  const [reload, setReload] = useState(0);

  const reloadNow = useCallback(() => setReload((prev) => prev + 1), []);

  return (
    <ReloadNowContext.Provider value={reloadNow}>
      <ReloadContext.Provider value={reload}>{children}</ReloadContext.Provider>
    </ReloadNowContext.Provider>
  );
}

// Returns the counter — put it straight into an effect dependency array.
function useReload(): number {
  const context = useContext(ReloadContext);

  if (context == null) {
    throw new Error("useReload must be used inside AppProvider.");
  }

  return context;
}

// Returns the bump function, stable for the lifetime of the app.
function useReloadNow(): () => void {
  const context = useContext(ReloadNowContext);

  if (!context) {
    throw new Error("useReloadNow must be used inside AppProvider.");
  }

  return context;
}

export { ReloadProvider, useReload, useReloadNow };
